import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Box, Tooltip, Typography } from "@mui/material";

/**
 * Two "is it up?" lights in the top bar: System (the backend
 * process) and Database. Both come from one poll of the backend's
 * unauthenticated `/api/health/` endpoint, which answers 200 with
 * `database: true` when the Django process runs and its database answers a
 * `SELECT 1`, and 503 with `database: false` when the process runs but the
 * database does not. Anything else (nginx 502 because the backend is down,
 * DNS/network failure, timeout) means the System is offline — and then the
 * database state cannot be known, so it is shown as unknown rather than
 * guessed.
 *
 * The request deliberately goes through `fetch`, not the shared axios
 * instance: the axios interceptor attaches the stored token to every call,
 * and a stale token must not influence the probe.
 */

const POLL_INTERVAL_HEALTHY_MS = 30_000;
// While something is wrong, check more often so recovery shows quickly.
const POLL_INTERVAL_PROBLEM_MS = 5_000;
const REQUEST_TIMEOUT_MS = 5_000;

const COLORS = {
  checking: "#9ca3af",
  online: "#22c55e",
  offline: "#ef4444",
  unknown: "#9ca3af",
};

const STATUS_WORD = {
  checking: "Checking…",
  online: "Online",
  offline: "Offline",
  unknown: "Unknown",
};

const SYSTEM_DETAIL = {
  checking: "Contacting the backend.",
  online: "The backend answers.",
  offline: "The backend does not answer.",
};

const DATABASE_DETAIL = {
  checking: "",
  online: "The database answers.",
  offline: "The backend cannot reach its database.",
  unknown: "The database state is unknown while the backend is offline.",
};

/** `/api/health/` resolved against the same base URL axios uses. */
const healthUrl = () => {
  const base = (axios.defaults.baseURL || "").replace(/\/+$/, "");
  return `${base}/api/health/`;
};

/** One probe → { system, database }. Never throws. */
async function probeBackend() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(healthUrl(), {
      method: "GET",
      cache: "no-store",
      signal: controller.signal,
    });
    let body = null;
    try {
      body = await response.json();
    } catch (error) {
      body = null;
    }
    // Only the health payload itself proves the backend is up; a proxy
    // error page (502/504) has no `database` field.
    if (body && typeof body.database === "boolean") {
      return { system: "online", database: body.database ? "online" : "offline" };
    }
    return { system: "offline", database: "unknown" };
  } catch (error) {
    return { system: "offline", database: "unknown" };
  } finally {
    clearTimeout(timer);
  }
}

const formatTime = (date) =>
  date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });

function StatusLine({ name, state }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          flexShrink: 0,
          borderRadius: "50%",
          bgcolor: COLORS[state],
          boxShadow: `0 0 0 3px ${COLORS[state]}40`,
          transition: "background-color 300ms, box-shadow 300ms",
        }}
      />
      <Typography
        variant="caption"
        sx={{ color: "rgba(255,255,255,0.75)", fontWeight: 600, lineHeight: 1.2 }}
      >
        {name}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: "rgba(255,255,255,0.5)",
          fontWeight: 500,
          lineHeight: 1.2,
          // The light alone tells the state on a narrow screen.
          display: { xs: "none", md: "block" },
        }}
      >
        {STATUS_WORD[state]}
      </Typography>
    </Box>
  );
}

export default function BackendStatus() {
  const [status, setStatus] = useState({ system: "checking", database: "checking" });
  const [checkedAt, setCheckedAt] = useState(null);
  const inFlight = useRef(false);
  const timerRef = useRef(null);

  const check = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const result = await probeBackend();
    inFlight.current = false;
    // Same value object only when nothing changed, so React can skip the
    // re-render (and the effect below its re-scheduling).
    setStatus((previous) =>
      previous.system === result.system && previous.database === result.database
        ? previous
        : result
    );
    setCheckedAt(new Date());
  }, []);

  const healthy = status.system === "online" && status.database === "online";
  const checking = status.system === "checking";

  useEffect(() => {
    let cancelled = false;

    const schedule = () => {
      clearTimeout(timerRef.current);
      const delay = healthy ? POLL_INTERVAL_HEALTHY_MS : POLL_INTERVAL_PROBLEM_MS;
      timerRef.current = setTimeout(async () => {
        if (cancelled) return;
        // Skip the periodic check while the tab is hidden; the visibility
        // handler below fires one as soon as the tab is shown again.
        if (document.visibilityState !== "hidden") await check();
        if (!cancelled) schedule();
      }, delay);
    };

    if (checking) check();
    schedule();

    const checkNow = () => {
      if (document.visibilityState === "hidden") return;
      check();
    };
    window.addEventListener("focus", checkNow);
    window.addEventListener("online", checkNow);
    document.addEventListener("visibilitychange", checkNow);
    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
      window.removeEventListener("focus", checkNow);
      window.removeEventListener("online", checkNow);
      document.removeEventListener("visibilitychange", checkNow);
    };
  }, [healthy, checking, check]);

  const detail = [SYSTEM_DETAIL[status.system], DATABASE_DETAIL[status.database]]
    .filter(Boolean)
    .join(" ");
  const tooltip = checkedAt ? `${detail} Checked ${formatTime(checkedAt)}.` : detail;

  return (
    <Tooltip title={tooltip} placement="bottom" arrow>
      <Box
        role="status"
        aria-live="polite"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 1.5,
          py: 0.6,
          borderRadius: 999,
          bgcolor: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.1)",
          cursor: "default",
        }}
      >
        <StatusLine name="System" state={status.system} />
        <Box sx={{ width: "1px", alignSelf: "stretch", bgcolor: "rgba(255,255,255,0.12)" }} />
        <StatusLine name="Database" state={status.database} />
      </Box>
    </Tooltip>
  );
}
